<template>
  <div
    class="ux-content-search"
    @keydown="handleKeydown"
    :class="{ invisible: !type }"
  >
    <div class="ux-content-search-selected-items" v-if="list">
      <div
        v-for="(selectedItem, index) in model"
        :key="index"
        class="ux-content-search-selected-item"
      >
        <slot name="selected">{{ selectedItem.title }}</slot>
      </div>
    </div>
    <search
      v-model="keywords"
      @focus="isFocused = true"
      @blur="handleBlur"
      ref="input"
      :placeholder="placeholderText"
      :loading="searching"
    />
    <ux-list
      v-if="showOptions && options.length"
      class="ux-content-search-options"
    >
      <ux-list-item
        v-for="(option, index) in options"
        :key="option._id || index"
        :class="{ active: index === activeIndex }"
        @click="selectItem(option)"
        @mouseover="activeIndex = index"
        class="ux-content-search-option"
      >
        <slot name="option">
          <flex-row gap center>
            <flex-cell shrink>
              <div class="face">
                <avatar :id="option._id" />
              </div>
            </flex-cell>
            <flex-cell>
              {{ option.title }}
            </flex-cell>
          </flex-row>
        </slot>
      </ux-list-item>
    </ux-list>
  </div>
</template>

<script>
import Search from "../../form/inputs/search.vue";

export default {
  components: {
    Search,
  },
  props: {
    modelValue: {
      type: Array,
      default() {
        return [];
      },
    },
    type: {
      type: String,
      required: true,
    },
    placeholder: {
      type: String,
    },
    multiple: {
      type: Boolean,
      default() {
        return false;
      },
    },
    list: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      keywords: "",
      options: [],
      model: this.modelValue.slice(),
      activeIndex: -1,
      isFocused: false,
      showOptions: false,
      glossary: {},
      searching: false,
    };
  },
  async created() {
    this.glossary = await this.$sdk.content.glossary({ hash: true });
  },
  computed: {
    placeholderText() {
      if (this.multiple) {
        return (
          this.placeholder ||
          `Search to select ${this.definition?.plural || "items"}` ||
          "Search to add items"
        );
      } else {
        return (
          this.placeholder ||
          `Search for an ${this.definition?.title || "item"}` ||
          "Search for an item"
        );
      }
    },
    definition() {
      const match = this.glossary[this.type];
      return match;
    },
  },
  watch: {
    modelValue(m) {
      if (!Array.isArray(m)) {
        if (m._id) {
          m = [m];
        } else {
          m = [];
        }
      }
      this.model = (m || []).slice();
    },
    keywords: {
      immediate: true,
      async handler(search) {
        this.searching = true;
        this.options = [];
        this.showOptions = false;
        if (!search || !this.type) {
          this.searching = false;
          return;
        }

        // Search for the things
        const { items } = await this.$sdk.content.list(this.type, {
          search,
          select: ["title", "firstName", "lastName"],
        });

        this.options = items;
        this.showOptions = true;
        this.activeIndex = this.options.length > 0 ? 0 : -1;
        this.searching = false;
      },
    },
    options(newOptions) {
      // Reset active index when options change
      this.activeIndex = newOptions.length > 0 ? 0 : -1;
    },
  },
  methods: {
    focus() {
      this.$refs.input.focus();
    },
    dispatch() {
      console.log("Update model value");
      this.$emit("update:modelValue", this.model);
    },
    handleKeydown(event) {
      if (!this.showOptions || !this.options.length) return;

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          this.activeIndex = (this.activeIndex + 1) % this.options.length;
          this.$nextTick(() => {
            const activeElement = this.$el.querySelector(
              ".ux-content-search-option.active",
            );
            if (activeElement) {
              activeElement.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
              });
            }
          });
          break;
        case "ArrowUp":
          event.preventDefault();
          this.activeIndex =
            this.activeIndex <= 0
              ? this.options.length - 1
              : this.activeIndex - 1;
          this.$nextTick(() => {
            const activeElement = this.$el.querySelector(
              ".ux-content-search-option.active",
            );
            if (activeElement) {
              activeElement.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
              });
            }
          });
          break;
        case "Enter":
          event.preventDefault();
          if (this.activeIndex >= 0 && this.activeIndex < this.options.length) {
            this.selectItem(this.options[this.activeIndex]);
          }
          break;
        case "Escape":
          this.showOptions = false;
          this.$refs.input.$el.blur();
          break;
      }
    },
    selectItem(option) {
      // Add the selected item to the model if it's not already there
      const isAlreadySelected = this.model.some(
        (item) => item._id === option._id,
      );

      if (!isAlreadySelected) {
        // If it's only single
        if (!this.multiple) {
          // Wipe the list
          this.model = [option];
        } else {
          this.model.push(option);
        }
        this.dispatch();
      }

      // Clear the search and options
      this.keywords = "";
      this.options = [];
      this.showOptions = false;

      // Focus back on the search input for better UX
      this.$nextTick(() => {
        this.$refs.input.$el.focus();
      });
    },
    handleBlur() {
      // Delay hiding options to allow click events to register
      setTimeout(() => {
        this.isFocused = false;
        this.showOptions = false;
      }, 200);
    },
  },
  mounted() {
    // Add a click outside listener to close the options
    document.addEventListener("click", (event) => {
      const component = this.$el;
      if (component && !component.contains(event.target)) {
        this.showOptions = false;
      }
    });
  },
  beforeUnmount() {
    // Clean up event listener
    document.removeEventListener("click", this.handleClickOutside);
  },
};
</script>

<style lang="scss" scoped>
.invisible {
  opacity: 0.5;
  pointer-events: none;
}
.search-component {
  position: relative;
  width: 100%;
}

.face {
  width: 20px;
}

.ux-content-search-selected-items {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}

.ux-content-search-selected-item {
  background-color: #f0f0f0;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 14px;
}

.ux-content-search-options {
  position: absolute;
  max-height: 20vh;
  overflow-y: auto;
  z-index: 100;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border: 1px solid color-mix(in lab, currentColor, var(--bg1));
  border-radius: 4px;
  margin-top: 4px;
  background-color: var(--bg1);
}

.ux-content-search-option.active {
  background-color: color-mix(in lab, var(--primary) 15%, var(--bg1));
}
</style>
